// client/src/coms/e2ee/useChannelE2ee.js
// Encryption for one server channel on this device: the server's policy (useServerE2ee), the channel's key members,
// whether this device has set up the channel's encrypted session (only then is there a lock), decryption of
// encrypted messages, and encryption of outgoing ones. When the server is encrypted and this device is not ready,
// sending is blocked; nothing is ever sent unencrypted in its place.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { comsApiGet } from '../comsApi';
import {
  cryptoAvailable,
  decryptMessages,
  decryptedResult,
  encryptForChannel,
  forgetFailedResults,
  onE2eeChange,
  prepareThread,
  rememberChannelPlaintext,
  retryThread,
  threadSession,
} from './cryptoSession';
import { requestEncryptionConsent } from './EncryptionConsentHost';
import { withEncryptedView } from './EncryptedMessageView';
import { ackServerE2ee, useServerE2ee } from './serverE2eeClient';

export default function useChannelE2ee({ serverKey, channelDbId, channelKind, messagesRaw }) {
  const { data: server, refresh: refreshServer } = useServerE2ee(serverKey);
  const [info, setInfo] = useState(null);
  const [tick, setTick] = useState(0);
  const [decryptTick, setDecryptTick] = useState(0);
  const [retryTick, setRetryTick] = useState(0);
  const isText = channelKind === 'text' && !!channelDbId;
  const key = `ch:${channelDbId}`;

  useEffect(() => onE2eeChange(() => setTick((n) => n + 1)), []);

  const loadInfo = useCallback(async () => {
    if (!isText) return null;
    const d = await comsApiGet(`/text-channels/${encodeURIComponent(channelDbId)}/e2ee`);
    setInfo(d);
    return d;
  }, [isText, channelDbId]);

  const serverVersion = server ? server.version : 0;
  useEffect(() => {
    setInfo(null);
    if (!isText) return undefined;
    loadInfo().catch(() => {});
    const t = setInterval(() => loadInfo().catch(() => {}), 15000);
    return () => clearInterval(t);
  }, [isText, loadInfo, serverVersion]);

  const serverOn = !!(server && server.encryption === 'on');
  const pending = (server && server.pending_ack) || (info && info.pending_ack) || null;
  const active = !!(isText && info && info.status === 'e2ee' && !pending && info.members.includes(info.self));
  const local = useMemo(() => {
    if (!active) return null;
    if (!cryptoAvailable()) return { state: 'unsupported', error: '' };
    return threadSession(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, key, tick]);

  useEffect(() => {
    if (active && cryptoAvailable()) prepareThread(key, info);
  }, [active, key, info]);

  // Decrypt encrypted messages (also earlier ones after encryption was turned off). Only on a browser that already
  // has this account's encryption device: browsing never creates one (messages show a placeholder instead).
  const hasEncrypted = (messagesRaw || []).some((m) => m && m.encryption === 'e2ee');
  useEffect(() => {
    if (!hasEncrypted || !channelDbId) return undefined;
    const self = (info && info.self) || '';
    const serverName = self.split(':').slice(1).join(':') || 'voxemera.com';
    const room = { room_id: (info && info.room_id) || `!ch${channelDbId}:${serverName}`, self, members: self ? [self] : [] };
    if (!room.self) return undefined;
    decryptMessages(room, messagesRaw, (uid) => `@u${uid}:${serverName}`, { allowNewDevice: false, keyPrefix: 'ch:', eventPrefix: '$vxch', senderField: 'user_id' }).then((changed) => {
      // Redraw even if this effect re-ran meanwhile: the results changed, and the next pass would not report it.
      if (changed) setDecryptTick((n) => n + 1);
    });
    return undefined;
  }, [hasEncrypted, messagesRaw, info, channelDbId, retryTick, tick]);

  useEffect(() => {
    if (!hasEncrypted) return undefined;
    const t = setInterval(() => {
      forgetFailedResults();
      setRetryTick((n) => n + 1);
    }, 15000);
    return () => clearInterval(t);
  }, [hasEncrypted]);

  const displayRows = useMemo(
    () => (messagesRaw || []).map((m) => (m && m.encryption === 'e2ee' && !m.system_kind ? withEncryptedView(m, decryptedResult(`ch:${m.id}`)) : m)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messagesRaw, decryptTick]
  );

  // Encrypts for the channel's current key members (fetched fresh, so new members get keys too).
  const encryptMessage = useCallback(
    async (text) => {
      const fresh = await loadInfo();
      if (!fresh || fresh.status !== 'e2ee') throw Object.assign(new Error('The encryption status of this channel changed. Your message was not sent.'), { code: 'e2ee_changed' });
      const out = await encryptForChannel(channelDbId, fresh, text);
      return { ...out, remember: (messageId) => rememberChannelPlaintext(messageId, text, out.deviceId) };
    },
    [loadInfo, channelDbId]
  );

  const acknowledge = useCallback(async () => {
    if (!pending || !server) return;
    if (pending.action === 'enabled') {
      const r = await requestEncryptionConsent({ mode: 'accept', server_name: (server && server.server_name) || '', excluded: server.excluded });
      if (!r.accepted) return;
    }
    await ackServerE2ee(serverKey, pending.version);
    await refreshServer(true);
    await loadInfo().catch(() => {});
  }, [pending, server, serverKey, refreshServer, loadInfo]);

  const retry = useCallback(() => {
    if (info) retryThread(key, info);
  }, [info, key]);

  return {
    server,
    info,
    serverOn,
    pending,
    active,
    local,
    // Sending is blocked: an unacknowledged change, or an encrypted channel this device has not set up.
    blocked: !!(isText && (pending || (serverOn && !(active && local && local.state === 'ready')))),
    displayRows,
    encryptMessage: active ? encryptMessage : null,
    acknowledge,
    retry,
  };
}
